<template>
    <b-card no-body>
        <b-overlay :show="!supplier" rounded="sm" opacity="0.5">
            <div v-if="supplier">
                <div class="p-2">
                    <div class="row align-items-center">
                        <div class="col-sm-auto">
                            <div class="profile-image-wrapper">
                                <div class="profile-image">
                                    <b-avatar size="114" variant="light-secondary" :src="logo" />
                                </div>
                            </div>
                        </div>
                        <div class="col-sm ml-sm-2 mt-sm-0 mt-2 text-left">
                            <h3 class="font-weight-bolder mb-25">{{ supplier.Name }}</h3>
                            <div>
                                <b-badge v-if="supplier.FirmCode" class="profile-badge" variant="light-primary">
                                    Firma Kodu: {{ supplier.FirmCode }}
                                </b-badge>
                                <b-badge v-if="supplier.TaxNumber" class="profile-badge ml-50" variant="light-secondary">
                                    V.N.: {{ supplier.TaxNumber }}
                                </b-badge>
                            </div>
                        </div>
                    </div>
                </div>
                <hr class="m-0">

                <div class="p-2">
                    <h5 class="text-primary mb-1">Firma Bilgileri</h5>
                        <div class="row">
                            <div class="col-md-2 mb-1">
                                <label class="form-label-sm">Ana Firma</label>
                                <p class="font-weight-bold mb-0">{{ supplier.ParentFirmName || 'Yok' }}</p>
                            </div>

                            <div class="col-md-3 mb-1">
                                <label class="form-label-sm">Firma Rolü</label>
                                <p class="font-weight-bold mb-0">{{ supplier.FirmRoleStr || 'Genel' }}</p>
                            </div>

                            <div class="col-md-4 mb-1">
                                <label class="form-label-sm">Vergi Dairesi</label>
                                <p class="font-weight-bold mb-0">{{ taxAdministrationName || 'Belirtilmemiş' }}</p>
                            </div>

                            <div class="col-md-3 mb-1">
                                <label class="form-label-sm">Sertel Lojistik Firması mı?</label>
                                <p class="font-weight-bold mb-0">{{ supplier.IsOwnFirm ? 'Evet' : 'Hayır' }}</p>
                            </div>
                        </div>
                </div>

                <div class="mt-0 mb-2">
                    <h5 class="px-2 mb-1 text-primary">Adres Bilgileri</h5>
                    <b-overlay :show="loading" rounded="sm" opacity="0.5">
                        <v-table
                            v-if="locations"
                            name="supplier-locations-table"
                            :fields="locationFields"
                            :items="locations"
                            :with-items="true"
                            pagination="server"
                            :search="false"
                            :settings="false"
                        />
                    </b-overlay>
                </div>

                <div class="mb-2">
                    <h5 class="px-2  mb-1 text-primary">İletişim Bilgileri</h5>
                    <b-overlay :show="loading" rounded="sm" opacity="0.5">
                        <v-table
                            v-if="contacts"
                            name="supplier-contacts-table"
                            :fields="contactFields"
                            :items="contacts"
                            :with-items="true"
                            pagination="client"
                            :search="false"
                            :settings="false"
                        />
                    </b-overlay>
                </div>

            </div>
        </b-overlay>
    </b-card>
</template>

<script>
import { BCard, BRow, BCol, BAvatar, BBadge, BOverlay } from 'bootstrap-vue'
import FirmOperationsApi from '@/api/firm-operations';

export default {
    name: 'FuelSupplierDetail',
    components: {
        BCard, BRow, BCol, BAvatar, BBadge, BOverlay
    },
    props: {
        supplier: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            locations: null,
            contacts: null,
            loading: false,
            locationFields: [
                { name: 'Lokasyon Adı', key: 'LocationName', thClass: 'w-25', tdClass: 'text-nowrap'},
                { name: 'Adres', key: 'Address', thClass: 'w-50', tdClass: 'text-nowrap'},
                { name: 'İl / İlçe', key: 'provinceAndDistrict', thClass: 'w-25', tdClass: 'text-nowrap'},
            ],
            contactFields: [
                { name: 'Ad Soyad', key: 'fullName', thClass: 'w-25', tdClass: 'text-nowrap'}, 
                { name: 'Unvan', key: 'OccupationTitle', thClass: 'w-25', tdClass: 'text-nowrap'},
                { name: 'E-Posta', key: 'Email', thClass: 'w-25', tdClass: 'text-nowrap'},
                { name: 'Telefon', key: 'phoneInfo', thClass: 'w-25', tdClass: 'text-nowrap'}, 
            ]
        }
    },
    watch: {
        supplier: {
            handler(newSupplier) {
                if (newSupplier && newSupplier.Id) {
                    this.fetchDetails();
                }
            },
            immediate: true 
        }
    },
    computed: {
        logo() {
            if (this.supplier && this.supplier.Logo) {
                return this.supplier.Logo;
            }
            return require('@/assets/images/avatars/no-user-pic.svg');
        },
        taxAdministrationName() {
            return this.supplier?.TaxAdministrationName || 'Belirtilmemiş';
        }
    },
    methods: {
        fetchDetails() {
            this.loading = true;
            this.locations = null;
            this.contacts = null;

            const fullPaginationModel = {
                Take: 1000,
                Skip: 0,
                QueryParam: "", 
                IsAscending: false, 
                OrderColumnName: null 
            };

            const locationPromise = FirmOperationsApi.Location.Table({ PaginationRequestModel: fullPaginationModel, FirmId: this.supplier.Id });
            const contactPromise = FirmOperationsApi.Contact.Table({ PaginationRequestModel: fullPaginationModel, FirmId: this.supplier.Id });

            Promise.all([locationPromise, contactPromise])
                .then(([locationRes, contactRes]) => {
                    if (locationRes.data.Success) {
                        const formattedLocations = locationRes.data.Data.Data.map(location => ({
                            ...location, 
                            provinceAndDistrict: `${location.ProvinceName || ''} / ${location.DistrictName || ''}` 
                        }));
                        this.locations = formattedLocations;
                    }

                    if (contactRes.data.Success) {
                        const formattedContacts = contactRes.data.Data.Data.map(contact => ({
                            ...contact, 
                            fullName: `${contact.FirstName || ''} ${contact.LastName || ''}`.trim(), 
                            phoneInfo: contact.MobilePhone || contact.Phone 
                        }));
                        this.contacts = formattedContacts;
                    }
                }).finally(() => {
                this.loading = false;
            });
        }
    }
}
</script>

<style scoped>
.profile-image-wrapper {
    display: flex;
    justify-content: center;
}
.profile-image-wrapper .profile-image {
    border-radius: 0.5rem;
    background-color: #fff;
    padding: 0.5rem;
    border: 1px solid #ebe9f1;
}
.form-label-sm {
    font-size: 0.75rem;
    color: #6E6B7B;
    margin-bottom: 0.2rem;
    text-transform: uppercase;
}
p.font-weight-bold {
    margin-bottom: 0.5rem;
}
</style>